import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, loginWithGoogle } from "../lib/auth";
import {
  adminStats, adminApplications, adminUpdateApp,
  adminHelpRequests, adminUpdateHelp, adminReferrals,
  adminMembers, adminDonors, adminCheckins,
  adminWall, adminModeratePost,
  adminListEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent,
  adminUpdateStats, getStats,
  adminToolkitRequests, adminEventRsvps,
} from "../lib/api";
import { Loader2, ShieldAlert, Save, Plus, Trash2, X, Users } from "lucide-react";
import {
  ResourcesAdmin, BlogAdmin, LeadershipAdmin, ProgramStatusAdmin,
  EnrollmentsAdmin, PartnershipsAdmin,
} from "./AdminExtras";
import { NewHomeAdmin } from "./NewHomeAdmin";

function Stats() {
  const { data: s } = useQuery({ queryKey: ["admin-stats"], queryFn: adminStats });
  const cards = [
    ["Members", s?.members ?? 0],
    ["Pending applications", s?.applications_pending ?? 0],
    ["New enrollments", s?.enrollments_new ?? 0],
    ["New partnerships", s?.partnerships_new ?? 0],
    ["New help requests", s?.help_requests_new ?? 0],
    ["Referrals", s?.referrals ?? 0],
    ["Check-ins (7d)", s?.checkins_this_week ?? 0],
    ["Wall posts", s?.wall_posts ?? 0],
    ["Recurring donors", s?.donors_recurring ?? 0],
    ["Contact msgs (new)", s?.contact_new ?? 0],
    ["Resources", s?.resources ?? 0],
    ["Blog published", s?.blog_published ?? 0],
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map(([l, v]) => (
        <div key={l} className="bg-white border border-black/10 p-6" data-testid={`stat-${l.replace(/\s+/g, "-").toLowerCase()}`}>
          <p className="overline text-[#6B21A8]">{l}</p>
          <p className="mt-3 font-display text-4xl font-bold">{v}</p>
        </div>
      ))}
    </div>
  );
}

function AppsTable() {
  const [status, setStatus] = useState("pending");
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-apps", status], queryFn: () => adminApplications(status) });
  const update = async (id, s) => {
    try { await adminUpdateApp(id, s); toast.success(`Marked ${s}`); qc.invalidateQueries({ queryKey: ["admin-apps"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); } catch { toast.error("Failed"); }
  };
  return (
    <>
      <div className="mb-4 flex gap-2 flex-wrap">
        {["pending", "in_review", "approved", "denied", ""].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`rounded-full border px-3 py-1 text-xs ${status === s ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15"}`}>{s || "All"}</button>
        ))}
      </div>
      <div className="space-y-3">
        {data.map((a) => (
          <div key={a.id} className="bg-white border border-black/10 p-4 flex flex-wrap gap-4 items-center" data-testid={`app-${a.id}`}>
            <div className="flex-1 min-w-[240px]">
              <p className="overline text-[#6B21A8]">{a.type}</p>
              <p className="font-semibold">
                {a.type === "mentee" ? `${a.student_first_name} ${a.student_last_name}` : `${a.first_name} ${a.last_name}`}
              </p>
              <p className="text-xs text-black/60">{a.email || a.parent_email} · {new Date(a.created_at).toLocaleDateString()}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
              a.status === "approved" ? "bg-green-100 text-green-800" :
              a.status === "denied" ? "bg-red-100 text-red-800" :
              a.status === "in_review" ? "bg-amber-100 text-amber-800" : "bg-black/5 text-black/70"
            }`}>{a.status}</span>
            <div className="flex gap-2">
              <button onClick={() => update(a.id, "in_review")} className="text-xs px-3 py-1 rounded-md border border-black/15 hover:bg-black/5">Review</button>
              <button onClick={() => update(a.id, "approved")} className="text-xs px-3 py-1 rounded-md bg-[#6B21A8] text-white">Approve</button>
              <button onClick={() => update(a.id, "denied")} className="text-xs px-3 py-1 rounded-md border border-red-500 text-red-600">Deny</button>
            </div>
          </div>
        ))}
        {data.length === 0 && <p className="text-sm text-black/60">No applications.</p>}
      </div>
    </>
  );
}

function HelpTable() {
  const [status, setStatus] = useState("new");
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-help", status], queryFn: () => adminHelpRequests(status) });
  const update = async (id, s) => { try { await adminUpdateHelp(id, s); qc.invalidateQueries({ queryKey: ["admin-help"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); toast.success(`Updated`); } catch { toast.error("Failed"); } };
  return (
    <>
      <div className="mb-4 flex gap-2 flex-wrap">
        {["new", "in_progress", "resolved", "closed", ""].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`rounded-full border px-3 py-1 text-xs ${status === s ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15"}`}>{s || "All"}</button>
        ))}
      </div>
      <div className="space-y-3">
        {data.map((h) => (
          <div key={h.id} className="bg-white border border-black/10 p-4" data-testid={`help-${h.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${h.urgency === "emergency" ? "bg-red-100 text-red-800" : h.urgency === "urgent" ? "bg-amber-100 text-amber-800" : "bg-black/5"}`}>{h.urgency}</span>
              <p className="font-semibold text-sm">{h.requester_name || "Anonymous"} · {(h.categories || []).join(", ")}</p>
              <span className="ml-auto text-xs text-black/60">{new Date(h.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-3 text-sm text-black/80 whitespace-pre-wrap">{h.details}</p>
            <p className="mt-2 text-xs text-black/50">{h.requester_email} · {h.requester_phone}</p>
            <div className="mt-4 flex gap-2 flex-wrap">
              {["in_progress", "resolved", "closed"].map((s) => (
                <button key={s} onClick={() => update(h.id, s)} className="text-xs px-3 py-1 rounded-md border border-black/15 hover:bg-black/5 capitalize">{s.replace("_", " ")}</button>
              ))}
            </div>
          </div>
        ))}
        {data.length === 0 && <p className="text-sm text-black/60">Nothing here.</p>}
      </div>
    </>
  );
}

function Table({ query, columns, testid }) {
  const { data = [] } = useQuery(query);
  return (
    <div className="overflow-x-auto bg-white border border-black/10">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-black/5">
          <tr>{columns.map((c) => <th key={c.key} className="text-left px-4 py-2 font-medium">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.id || row.user_id || row.session_id || i} className="border-t border-black/5">
              {columns.map((c) => <td key={c.key} className="px-4 py-2">{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="p-4 text-sm text-black/60">Nothing here.</p>}
    </div>
  );
}

function WallModerate() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-wall"], queryFn: adminWall });
  const flip = async (id, s) => { try { await adminModeratePost(id, s); qc.invalidateQueries({ queryKey: ["admin-wall"] }); toast.success(`Post ${s}`); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-3">
      {data.map((p) => (
        <div key={p.id} className="bg-white border border-black/10 p-4" data-testid={`mod-${p.id}`}>
          <p className="text-xs text-black/50">{p.author_name} · {new Date(p.created_at).toLocaleString()} · <span className={p.status === "hidden" ? "text-red-600" : "text-green-700"}>{p.status}</span></p>
          <p className="mt-2 text-sm">{p.body}</p>
          <div className="mt-3 flex gap-2">
            <button onClick={() => flip(p.id, p.status === "hidden" ? "active" : "hidden")} className="text-xs px-3 py-1 rounded-md border border-black/15">
              {p.status === "hidden" ? "Unhide" : "Hide"}
            </button>
          </div>
        </div>
      ))}
      {data.length === 0 && <p className="text-sm text-black/60">No posts yet.</p>}
    </div>
  );
}

const EMPTY_EVENT = { title: "", date: "", time: "", location: "", description: "", category: "", image: "" };
const input = "w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#6B21A8]";

function EventForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...EMPTY_EVENT, ...(initial || {}) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setNum = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? null : Number(e.target.value) });
  const setBool = (k) => (e) => setF({ ...f, [k]: e.target.checked });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="bg-white border border-[#6B21A8] p-5 space-y-3" data-testid="event-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block"><span className="text-xs font-medium text-black/70">Title *</span><input required className={input} value={f.title} onChange={set("title")} data-testid="event-title" /></label>
        <label className="block"><span className="text-xs font-medium text-black/70">Date (YYYY-MM-DD) *</span><input required type="date" className={input} value={f.date} onChange={set("date")} data-testid="event-date" /></label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block"><span className="text-xs font-medium text-black/70">Time</span><input className={input} value={f.time} onChange={set("time")} data-testid="event-time" placeholder="10:00 AM – 3:00 PM" /></label>
        <label className="block"><span className="text-xs font-medium text-black/70">Category</span><input className={input} value={f.category} onChange={set("category")} data-testid="event-category" placeholder="Leadership, Meeting, Gala…" /></label>
      </div>
      <label className="block"><span className="text-xs font-medium text-black/70">Location</span><input className={input} value={f.location} onChange={set("location")} data-testid="event-location" /></label>
      <label className="block"><span className="text-xs font-medium text-black/70">Description</span><textarea rows="3" className={input} value={f.description} onChange={set("description")} data-testid="event-desc" /></label>
      <label className="block"><span className="text-xs font-medium text-black/70">Image URL (or /photos/…)</span><input className={input} value={f.image} onChange={set("image")} data-testid="event-image" placeholder="/photos/classroom-cohort.jpg" /></label>

      <fieldset className="border-t border-black/10 pt-4 mt-2 space-y-3">
        <legend className="text-xs uppercase tracking-widest text-[#6B21A8] font-bold">RSVP & Ticketing</legend>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!f.rsvp_enabled} onChange={setBool("rsvp_enabled")} className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" data-testid="event-rsvp-enabled" />
          <span className="text-sm">RSVPs enabled (uncheck for internal meetings / info-only listings)</span>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-black/70">Ticket price (USD) — leave blank for free</span>
            <input type="number" step="0.01" min="0" className={input} value={f.ticket_price ?? ""} onChange={setNum("ticket_price")} data-testid="event-price" placeholder="e.g. 50 for a paid gala" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-black/70">Capacity — leave blank for unlimited</span>
            <input type="number" min="1" className={input} value={f.capacity ?? ""} onChange={setNum("capacity")} data-testid="event-capacity" placeholder="e.g. 120" />
          </label>
        </div>
      </fieldset>

      <div className="flex gap-2 justify-end pt-2">
        <button type="button" onClick={onCancel} className="text-xs px-3 py-2 rounded-md border border-black/15" data-testid="event-cancel">Cancel</button>
        <button type="submit" disabled={saving} className="text-xs px-4 py-2 rounded-md bg-[#6B21A8] text-white flex items-center gap-1" data-testid="event-save">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
        </button>
      </div>
    </form>
  );
}

function EventRsvps({ event, onClose }) {
  const { data } = useQuery({ queryKey: ["admin-event-rsvps", event.id], queryFn: () => adminEventRsvps(event.id) });
  const rsvps = data?.rsvps || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="event-rsvps-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-hidden bg-white border-2 border-black flex flex-col">
        <div className="flex items-center justify-between border-b border-black/10 bg-[#0A0A0A] text-white px-5 py-3">
          <div>
            <p className="overline text-[#F5B90A]">RSVPs & Tickets</p>
            <p className="font-display text-lg font-semibold">{event.title}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-3 border-b border-black/10 flex flex-wrap gap-4 text-sm">
          <span><strong>{data?.confirmed ?? "…"}</strong> confirmed</span>
          {data?.capacity != null && <span>Capacity: <strong>{data.capacity}</strong> · Remaining: <strong>{data.remaining}</strong></span>}
        </div>
        <div className="overflow-y-auto p-5">
          {rsvps.length === 0 ? (
            <p className="text-sm text-black/60">No RSVPs yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-black/5">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Paid</th>
                </tr>
              </thead>
              <tbody>
                {rsvps.map((r) => (
                  <tr key={r.id} className="border-t border-black/5">
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2">{r.email}</td>
                    <td className="px-3 py-2">{r.quantity}</td>
                    <td className="px-3 py-2">{r.is_ticket ? "Ticket" : "RSVP"}</td>
                    <td className="px-3 py-2 capitalize">{r.status.replace("_", " ")}</td>
                    <td className="px-3 py-2">{r.amount_paid ? `$${r.amount_paid.toFixed(2)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function EventsAdmin() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-events"], queryFn: adminListEvents });
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewingRsvpsFor, setViewingRsvpsFor] = useState(null);

  const save = async (payload, id) => {
    setSaving(true);
    try {
      if (id) { await adminUpdateEvent(id, payload); toast.success("Event updated"); }
      else { await adminCreateEvent(payload); toast.success("Event created"); }
      setCreating(false); setEditingId(null);
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["events-all"] });
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try { await adminDeleteEvent(id); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-events"] }); qc.invalidateQueries({ queryKey: ["events"] }); qc.invalidateQueries({ queryKey: ["events-all"] }); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-black/60">{data.length} event{data.length === 1 ? "" : "s"}</p>
        <button onClick={() => { setCreating(true); setEditingId(null); }} className="btn-primary text-sm !py-2 !px-4 inline-flex items-center gap-2" data-testid="event-add">
          <Plus className="h-4 w-4" /> New event
        </button>
      </div>
      {creating && <EventForm initial={EMPTY_EVENT} onSave={(p) => save(p)} onCancel={() => setCreating(false)} saving={saving} />}
      <div className="space-y-3">
        {data.map((e) => (
          <div key={e.id} className="bg-white border border-black/10 p-4" data-testid={`event-row-${e.id}`}>
            {editingId === e.id ? (
              <EventForm initial={e} onSave={(p) => save(p, e.id)} onCancel={() => setEditingId(null)} saving={saving} />
            ) : (
              <div className="flex gap-4 items-start">
                {e.image && <img src={e.image} alt="" className="h-20 w-28 object-cover border border-black/10" />}
                <div className="flex-1 min-w-0">
                  <p className="overline text-[#6B21A8]">{e.category || "Event"}</p>
                  <p className="font-display text-lg font-semibold">{e.title}</p>
                  <p className="text-xs text-black/60">{e.date} · {e.time} · {e.location}</p>
                  <p className="text-xs text-black/70 mt-1 line-clamp-2">{e.description}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
                    {e.rsvp_enabled === false ? (
                      <span className="px-2 py-0.5 bg-black/10 text-black/60 rounded">RSVPs off</span>
                    ) : e.ticket_price > 0 ? (
                      <span className="px-2 py-0.5 bg-[#F5B90A] text-black rounded">Ticket ${e.ticket_price}</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-[#6B21A8]/10 text-[#6B21A8] rounded">Free RSVP</span>
                    )}
                    {e.capacity && <span className="px-2 py-0.5 bg-black/5 text-black/60 rounded">Cap {e.capacity}</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  <button onClick={() => setViewingRsvpsFor(e)} className="text-xs px-3 py-1 rounded-md border border-black/15 flex items-center gap-1" data-testid={`event-rsvps-${e.id}`}><Users className="h-3 w-3" /> RSVPs</button>
                  <button onClick={() => { setEditingId(e.id); setCreating(false); }} className="text-xs px-3 py-1 rounded-md border border-black/15" data-testid={`event-edit-${e.id}`}>Edit</button>
                  <button onClick={() => remove(e.id)} className="text-xs px-3 py-1 rounded-md border border-red-500 text-red-600 flex items-center gap-1" data-testid={`event-del-${e.id}`}><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {data.length === 0 && !creating && <p className="text-sm text-black/60">No events yet. Click "New event".</p>}
      </div>
      {viewingRsvpsFor && <EventRsvps event={viewingRsvpsFor} onClose={() => setViewingRsvpsFor(null)} />}
    </div>
  );
}

function StatsAdmin() {
  const qc = useQueryClient();
  const { data: stats = [] } = useQuery({ queryKey: ["stats"], queryFn: getStats });
  const [rows, setRows] = useState(null);
  const current = rows ?? stats;
  const [saving, setSaving] = useState(false);

  const update = (i, key, val) => {
    const copy = [...current];
    copy[i] = { ...copy[i], [key]: val };
    setRows(copy);
  };
  const addRow = () => setRows([...(rows ?? stats), { label: "", value: "" }]);
  const removeRow = (i) => setRows((rows ?? stats).filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = current.filter((r) => r.label.trim() && r.value.trim());
    setSaving(true);
    try {
      await adminUpdateStats(clean);
      toast.success("Stats updated");
      setRows(null);
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-black/60">These numbers appear in the "Our Impact" section on the homepage.</p>
      <div className="space-y-2" data-testid="stats-editor">
        {current.map((s, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
            <input className={input} value={s.label} placeholder="Label" onChange={(e) => update(i, "label", e.target.value)} data-testid={`stat-label-${i}`} />
            <input className={input} value={s.value} placeholder="Value" onChange={(e) => update(i, "value", e.target.value)} data-testid={`stat-value-${i}`} />
            <button onClick={() => removeRow(i)} className="text-black/50 hover:text-red-600" data-testid={`stat-del-${i}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <button onClick={addRow} className="text-sm px-4 py-2 rounded-md border border-black/15 inline-flex items-center gap-1" data-testid="stat-add"><Plus className="h-3 w-3" /> Add stat</button>
        <button onClick={save} disabled={saving} className="btn-primary text-sm !py-2 !px-4 inline-flex items-center gap-2" data-testid="stat-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("stats");

  if (loading) return <div className="container-x py-32 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!user) {
    return (
      <section className="container-x py-24 text-center max-w-xl">
        <ShieldAlert className="mx-auto h-14 w-14 text-[#6B21A8]" />
        <h1 className="mt-6 font-display text-4xl font-bold">Admin sign-in required.</h1>
        <button onClick={loginWithGoogle} className="btn-primary mt-8" data-testid="admin-sign-in">Sign in with Google</button>
      </section>
    );
  }
  if (user.role !== "admin") {
    return (
      <section className="container-x py-24 text-center max-w-xl">
        <ShieldAlert className="mx-auto h-14 w-14 text-red-600" />
        <h1 className="mt-6 font-display text-4xl font-bold">Not authorized.</h1>
        <p className="mt-3 text-black/70">Your account isn't on the admin allow-list.</p>
      </section>
    );
  }

  const tabs = [
    { key: "stats", label: "Overview" },
    { key: "new-home", label: "New Home Fund" },
    { key: "enrollments", label: "Enrollments" },
    { key: "partnerships", label: "Partnerships" },
    { key: "events", label: "Events" },
    { key: "site-stats", label: "Impact Stats" },
    { key: "program-status", label: "Program Status" },
    { key: "resources", label: "Resources" },
    { key: "blog", label: "Blog / News" },
    { key: "leadership", label: "Leadership" },
    { key: "toolkit", label: "Toolkit Requests" },
    { key: "apps", label: "Applications" },
    { key: "help", label: "Help Requests" },
    { key: "referrals", label: "School Referrals" },
    { key: "members", label: "Members" },
    { key: "donors", label: "Donors" },
    { key: "checkins", label: "Check-ins" },
    { key: "wall", label: "Wall Moderation" },
  ];

  return (
    <>
      <section className="bg-[#0A0A0A] text-white">
        <div className="container-x py-14">
          <span className="overline text-[#F5B90A]">Admin</span>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-bold">Dashboard</h1>
        </div>
      </section>
      <div className="sticky top-16 md:top-[73px] z-20 border-b border-black/10 bg-white">
        <div className="container-x flex gap-2 py-3 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium whitespace-nowrap ${tab === t.key ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15 text-black/70"}`}>{t.label}</button>
          ))}
        </div>
      </div>
      <section className="bg-[#FDFBF7]">
        <div className="container-x py-10">
          {tab === "stats" && <Stats />}
          {tab === "new-home" && <NewHomeAdmin />}
          {tab === "enrollments" && <EnrollmentsAdmin />}
          {tab === "partnerships" && <PartnershipsAdmin />}
          {tab === "events" && <EventsAdmin />}
          {tab === "site-stats" && <StatsAdmin />}
          {tab === "program-status" && <ProgramStatusAdmin />}
          {tab === "resources" && <ResourcesAdmin />}
          {tab === "blog" && <BlogAdmin />}
          {tab === "leadership" && <LeadershipAdmin />}
          {tab === "toolkit" && <Table
            query={{ queryKey: ["admin-toolkit"], queryFn: adminToolkitRequests }} testid="admin-toolkit"
            columns={[
              { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "org_type", label: "Role" },
              { key: "location", label: "Location", render: (r) => [r.city, r.state, r.country].filter(Boolean).join(", ") },
              { key: "audience_size", label: "Audience" },
              { key: "notes", label: "Notes" },
            ]} />}
          {tab === "apps" && <AppsTable />}
          {tab === "help" && <HelpTable />}
          {tab === "referrals" && <Table
            query={{ queryKey: ["admin-refs"], queryFn: adminReferrals }} testid="admin-referrals"
            columns={[
              { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
              { key: "student", label: "Student", render: (r) => `${r.student_first_name} ${r.student_last_name} (age ${r.student_age})` },
              { key: "referrer", label: "Referrer", render: (r) => `${r.referrer_name} — ${r.referrer_role} @ ${r.referrer_org}` },
              { key: "urgency", label: "Urgency" },
            ]} />}
          {tab === "members" && <Table
            query={{ queryKey: ["admin-members"], queryFn: adminMembers }} testid="admin-members"
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "role", label: "Role" },
              { key: "created_at", label: "Joined", render: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />}
          {tab === "donors" && <Table
            query={{ queryKey: ["admin-donors"], queryFn: adminDonors }} testid="admin-donors"
            columns={[
              { key: "donor_name", label: "Name" },
              { key: "donor_email", label: "Email" },
              { key: "amount", label: "Amount", render: (r) => `$${r.amount}` },
              { key: "lookup_key", label: "Type" },
              { key: "recurring", label: "Recurring", render: (r) => r.recurring ? "Yes" : "No" },
              { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />}
          {tab === "checkins" && <Table
            query={{ queryKey: ["admin-checkins"], queryFn: adminCheckins }} testid="admin-checkins"
            columns={[
              { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
              { key: "user_name", label: "Member" },
              { key: "feeling", label: "Feeling" },
              { key: "week_rating", label: "Week" },
              { key: "needs_help", label: "Needs help with", render: (r) => (r.needs_help || []).join(", ") || "—" },
              { key: "need_to_talk", label: "Wants to talk", render: (r) => r.need_to_talk ? "Yes" : "" },
            ]} />}
          {tab === "wall" && <WallModerate />}
        </div>
      </section>
    </>
  );
}
